'use client';

import { useSearchParams } from 'next/navigation';
import { useActionState } from 'react';
import { loginAction, type LoginState } from './actions';

const initial: LoginState = {};

export default function LoginPage() {
  const [state, action, pending] = useActionState(loginAction, initial);
  const reason = useSearchParams().get('reason');

  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950">
      <div className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-8 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-zinc-900 px-1.5 py-0.5 font-mono text-xs font-bold tracking-tight text-emerald-400 dark:bg-zinc-50 dark:text-emerald-600">
            0x
          </span>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">
            Helliwood
          </h1>
        </div>
        <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
          Bitte melde dich mit deinem Admin-Account an.
        </p>

        {reason === 'session-expired' && (
          <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.
          </p>
        )}

        <form action={action} className="mt-6 space-y-4">
          <Field
            label="E-Mail"
            name="email"
            type="email"
            autoComplete="username"
            defaultValue={state.values?.email}
            errors={state.fieldErrors?.email}
            required
          />
          <Field
            label="Passwort"
            name="password"
            type="password"
            autoComplete="current-password"
            errors={state.fieldErrors?.password}
            required
          />
          {state.formError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
              {state.formError}
            </p>
          )}
          <button
            type="submit"
            disabled={pending}
            className="w-full rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            {pending ? 'Anmelden…' : 'Anmelden'}
          </button>
        </form>
      </div>
    </main>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  errors?: string[];
}

function Field({ label, name, errors, ...rest }: FieldProps) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
        {label}
      </span>
      <input
        {...rest}
        name={name}
        className="mt-1 block w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-50 dark:focus:border-zinc-200 dark:focus:ring-zinc-200/10"
      />
      {errors?.map((e) => (
        <span
          key={e}
          className="mt-1 block text-xs text-red-600 dark:text-red-400"
        >
          {e}
        </span>
      ))}
    </label>
  );
}
